import { useTObject } from "@/i18n"
import { DataDrivenFormSchema } from "@/lib/data-driven-forms/schema"
import { EducatorsReliefListingsPriorities } from "@/types/serializers"
import { TagHierarchy } from "@/types/tag-hierarchy"
import { useTranslation } from "react-i18next"
import { validationSchema } from "./validation-schema"
import { typedEntries } from "@/types/helpers"
import { ParseKeys } from "i18next"
import { Globe, Lock } from "lucide-react"

export function useFormSchema(
  priorities: Array<EducatorsReliefListingsPriorities>,
) {
  const tagCategories = useTObject(
    "tag_categories",
    "listing_profile_tags",
  ) as unknown as TagHierarchy
  const { t } = useTranslation("educator_relief_listings", {
    keyPrefix: "form",
  })

  const priorityOptions: Array<SelectOption<string>> = priorities.map(
    (priority) => {
      const calendarStartInstant = Temporal.Instant.from("2025-01-06T00:00:00Z")

      const localisedSessions = priority.sessions.map((session) => {
        const daySeconds = 86400 * session.day!
        const timeSeconds = Temporal.PlainTime.from(session.start_time!)
          .since("00:00")
          .total({ unit: "seconds" })
        return calendarStartInstant
          .add({ seconds: daySeconds + timeSeconds })
          .toLocaleString(undefined, {
            weekday: "long",
            hour: "numeric",
            hour12: true,
            timeZone: "UTC",
          })
      })

      return {
        label: `${priority.custom_class_name} ${t("priority_label", { priority: priority.priority, index: priority.instance_index + 1 })} - ${localisedSessions.join(", ")}`,
        value: priority.uuid,
      }
    },
  )

  const formSchema: DataDrivenFormSchema<
    ["educator_relief_listings", "listing_profile_tags"]
  > = {
    type: "form",
    validator: validationSchema,
    namespace: ["educator_relief_listings", "listing_profile_tags"],
    properties: [
      {
        type: "section:hash",
        sectionKey: "details",
        title: "form.sections.details",
        properties: [
          {
            type: "field:input",
            inputType: "text",
            name: "details.title",
            label: "form.details_section.title",
          },
          {
            type: "field:textarea",
            name: "details.description",
            label: "form.details_section.description",
          },
          {
            type: "field:radioGroup",
            name: "invite_only",
            label: "form.details_section.invite_only",
            useOptions: () => {
              const { t } = useTranslation("school_relief_listings")

              return [
                {
                  label: t("privacy_levels.invite_only"),
                  description: t("privacy_levels.invite_only_description"),
                  value: "true",
                  icon: Lock,
                },
                {
                  label: t("privacy_levels.anyone_can_apply"),
                  description: t("privacy_levels.anyone_can_apply_description"),
                  value: "false",
                  icon: Globe,
                },
              ]
            },
          },
          {
            type: "field:input",
            inputType: "date",
            name: "start_date",
            label: "form.details_section.start_date",
          },
          {
            type: "field:input",
            inputType: "date",
            name: "end_date",
            label: "form.details_section.end_date",
          },
          {
            type: "field:combobox",
            comboboxType: "single",
            name: "priority_uuid",
            label: "form.details_section.priority",
            useOptions: () => priorityOptions,
          },
        ],
      },
      {
        type: "section:hash",
        sectionKey: "tags",
        title: "form.sections.tags",
        infoContentKey: "form.tags_section.section_description",
        properties: typedEntries(tagCategories).map(
          ([tagCategoryKey, category]) => ({
            type: "section:scrolling",
            sectionKey: tagCategoryKey,
            title:
              `listing_profile_tags:tag_categories.${tagCategoryKey}.name` as ParseKeys<
                ["educator_relief_listings", "listing_profile_tags"]
              >,
            infoContentKey:
              `listing_profile_tags:tag_categories.${tagCategoryKey}.description` as ParseKeys<
                ["educator_relief_listings", "listing_profile_tags"]
              >,
            properties: typedEntries(category.tag_subcategories).flatMap(
              ([tagSubCategoryKey, subcategory]) =>
                typedEntries(subcategory.tags).map(([tagKey, _tag]) => ({
                  type: "field:custom:toggle",
                  name: "details.tag_ids",
                  label:
                    `listing_profile_tags:tag_categories.${tagCategoryKey}.tag_subcategories.${tagSubCategoryKey}.tags.${tagKey}.name` as ParseKeys<
                      ["educator_relief_listings", "listing_profile_tags"]
                    >,
                  value: tagKey,
                  isArray: true,
                })),
            ),
          }),
        ),
      },
    ],
  }
  return formSchema
}
